<template>
  <div class="path-editor-root" v-on-clickaway="closeMenu">
    <div
      :contenteditable="disabled ? false : true"
      @input="handleUserChange()"
      ref="editor"
      class="path-editor monospaced"
      :class="{ errored: editorError }"
      :data-error="editorError"
      @contextmenu.stop.prevent="pathClicked"
      @mouseup="mouseUp"
      @mouseup.left="closeMenu"
      @keyup="keyUp"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
    ></div>
    <div class="path-info-menu" ref="menu">
      <PathEditorMenu
        v-show="menuToken"
        :token="menuToken"
        :resolved="resolvedInfo"
        :description="selectedTokenDescription"
        @token-update="handleTokenUpdate"
        @close="closeMenu"
        :app="app"
      />
    </div>
  </div>
</template>
<style lang="scss">
// Note: Not 'scoped' because we manually insert elements with classes
@charset "utf-8";

.path-editor-root {
  display: contents; // Browser pretends contents were directly in the parent container, but this still gets to be the root element of the vue component

  $editor-line-height: 1em;
  $editor-padding: 0.3em;

  .path-editor {
    width: 100%;
    line-height: $editor-line-height;
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 0.28571429rem;
    color: black;
    outline: none;
    transition: border-color 0.1s ease;
    padding: $editor-padding;

    // Hack: If used elsewhere, these corners should probably be round
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;

    &.errored {
      border-color: rgba(183, 0, 0, 0.2);
      background-color: rgba(183, 0, 0, 0.06);
    }

    &:focus {
      border-color: #85b7d9;
      &.errored {
        border-color: rgba(183, 0, 0, 0.6);
        background-color: rgba(183, 0, 0, 0.06);
      }

      box-shadow: 0 0 0 0 rgba(34, 36, 38, 0.35) inset;
    }

    .path-slash {
      padding: 0 5px;
    }

    .border-gradient {
      border: 2px solid;
      border-image-slice: 1;
      border-width: 1px;
    }

    .match.has-hint {
      border-radius: 0 4px 4px 0;
    }

    .match.has-index,
    .match.includes-invisible {
      border-radius: 4px 0 0 4px;
    }

    .match.has-hint.has-index {
      border-radius: 0px;
    }

    .hint {
      border-radius: 4px 0 0 4px;
      border: 1px dotted gray;
      border-right-width: 0;
    }

    .index {
      border-radius: 0 4px 4px 0;
      border: 1px dotted gray;
      border-left-width: 0;
    }

    .index.includes-invisible {
      border-radius: 0px;
    }

    .include-invisible {
      border-radius: 0 4px 4px 0;
      border: 1px dotted gray;
      border-left-width: 0;
    }

    .match {
      border-radius: 4px;
      border: 1px dotted #2185d0;
      color: #2185d0;
    }

    .match,
    .index,
    .hint,
    .includes-invisible {
      &.has-path-info {
        border-style: solid;
      }
    }

    .marker {
      opacity: 0.3;
    }

    .token {
      // no breaks inside this
      white-space: nowrap;

      &.shows-menu {
        .exact {
          background-color: #2185d010;
        }

        .window {
          background-color: #53009910;
        }

        .wildcard,
        .deepwild,
        .index,
        .hint,
        .include-invisible {
          background-color: #99999910;
        }

        .contains,
        .endswith,
        .startswith {
          background-color: #caa50010;
        }

        .regex {
          background-color: #20970810;
        }
      }
    }

    .match,
    .hint,
    .index,
    .relation,
    .include-invisible {
      display: inline-block;
      font-weight: normal;
      font-size: 0.85714286rem;

      margin: 1px 0;

      padding-left: calc(0.833em - 1px);
      padding-right: calc(0.833em - 1px);
      padding-top: calc(0.5em - 1px);
      padding-bottom: calc(0.5em - 1px);

      &.has-path-info {
        border-style: solid !important;
      }

      &.window {
        border: 1px dotted #530099;
        color: #530099;
        margin-right: 0.5em;
      }

      &.wildcard,
      &.deepwild {
        border: 1px dotted #999;
        color: #999;
      }

      &.contains,
      &.endswith,
      &.startswith {
        border: 1px dotted #caa500;
        color: #caa500;
      }

      &.regex {
        border: 1px dotted #209708;
        color: #209708;
      }

      // The window token doesn't have a slash to give spacing to the neighbor on the right
      &.window-token {
        margin-right: 5px;
      }
    }
  }

  .path-info-menu {
    position: absolute !important; // Overrule fomantic so we can position dynamically
  }
}
</style>

<script>
// @flow
import _ from 'lodash'
import parser from './Path.pegjs'
import PathEditorMenu from './PathEditorMenu'
import { mixin as clickaway } from 'vue-clickaway'

//
// The main challenge of this component is to achieve a seamless editing experience where the path gets rendered correctly
// without disturbing the cursor position in the process. For the cursor behavior, we go low-level (non-vue)
//

export default {
  name: 'path-editor',
  components: { PathEditorMenu },
  mixins: [clickaway],
  props: ['path', 'pathInfos', 'disabled', 'app'],

  data() {
    return {
      menuIndex: -1,
      menuTarget: null,
      selectingForIndex: -1,
      editorError: null,
      previousPath: '',
      currentPathInfos: this.pathInfos || [],
      windowPathInfo: null,
      pathIndexAtCaret: -1,
      currentTokens: []
    }
  },

  computed: {
    selectedTokenDescription() {
      return this.menuIndex < 0 ? '' : this.describe(this.currentTokens[this.menuIndex])
    },
    menuToken() {
      return this.menuIndex < 0 ? null : this.currentTokens[this.menuIndex]
    },
    resolvedInfo() {
      return (this.currentPathInfos?.length ?? -1) < 0
        ? null
        : this.currentPathInfos[this.menuIndex]
    }
  },

  methods: {
    handleTokenUpdate(token) {
      this.currentTokens[this.menuIndex] = token
      this.handleUserChange(getPathFromTokens(this.currentTokens))
    },

    updatePathIndexFromCaretPosition() {
      this.pathIndexAtCaret = this.findIndex(
        (window.getSelection && window.getSelection())?.anchorNode?.parentElement
      )
    },

    mouseUp() {
      this.updatePathIndexFromCaretPosition()
    },

    keyUp(evt) {
      this.updatePathIndexFromCaretPosition()
    },

    closeMenu() {
      this.menuIndex = -1
      this.menuTarget?.classList.remove('shows-menu')
      this.menuTarget = null
      this.selectingForIndex = -1
    },

    // Find the ancestor with a data-index attribute and return its value
    findIndex(elm) {
      if (!elm) return -1
      if (elm.hasAttribute('data-index')) {
        return parseInt(elm.getAttribute('data-index'), 10)
      }
      return this.findIndex(elm.parentElement)
    },

    // Find the ancestor with class 'token' and return it
    findToken(elm) {
      if (!elm) return null
      if (elm.classList.contains('token')) {
        return elm
      }
      return this.findToken(elm.parentElement)
    },

    placeMenu(elm) {
      if (!elm) return
      const menuElt = this.$refs.menu
      menuElt.style.left = elm.offsetLeft + 'px'
      menuElt.style.top = elm.offsetTop + 25 + 'px'
    },

    pathClicked(evt) {
      const idx = this.findIndex(evt.target)
      // only show menu if we click where the caret is
      //if (idx !== this.pathIndexAtCaret && this.pathIndexAtCaret !== -1) return

      const clickedEltRect = evt.target.getBoundingClientRect()
      const clickedTokenValue = getInnerText(evt.target)

      const tokenElm = this.findToken(evt.target)

      if (!tokenElm) {
        return
      }

      this.placeMenu(tokenElm)

      this.menuTarget?.classList.remove('shows-menu')
      this.menuTarget = tokenElm
      this.menuTarget.classList.add('shows-menu')
      this.menuIndex = idx

      this.$refs.editor.focus() // So that we can have a blur event when user clicks elsewhere
    },

    clearEditor(editor) {
      while (editor.firstChild) {
        editor.removeChild(editor.lastChild)
      }
    },

    updateEditor(editor, newPath) {
      if (this.previousPath === newPath) return
      try {
        var tokens = getTokensFromPath(newPath)
        var cursorIndex = saveCursorIndex(editor)
        this.clearEditor(editor)
        this.detectDeletedTokens(this.previousPath, newPath)
        this.previousPath = newPath
        this.generateAndInsertNewDom(tokens)
        restoreCursorPosition(cursorIndex, editor)
        this.editorError = null
      } catch (e) {
        this.editorError = e
        console.error('error updating editor', newPath, e)
      }
    },

    handleUserChange(programmaticValue, suppressEmission = false) {
      var editor = this.$refs.editor
      const newPath = programmaticValue != null ? programmaticValue : getInnerText(editor)
      if (!suppressEmission) this.$emit('change', newPath)
      this.updateEditor(editor, newPath)
    },

    detectDeletedTokens(oldPath, newPath) {
      const isEqual = (token1, token2) => {
        return (
          token1?.type === token2?.type &&
          (token1?.text === token2?.text || token1?.title === token2?.title)
        )
      }
      if (!this.currentPathInfos || this.currentPathInfos.length === 0) return
      // Detect deleted tokens and remove them from the pathInfos array
      const oldTokens = getTokensFromPath(oldPath)
      const newTokens = getTokensFromPath(newPath)
      if (oldTokens.length <= newTokens.length) return // Doesn't look like any tokens were deleted
      const newPathInfos = []
      let numDeleted = 0
      for (let i = 0; i < oldTokens.length; i++) {
        let newToken = newTokens[i - numDeleted]
        // Traverse forwards in oldTokens until a match is found for the newTokens[i] value. All indexes in between are deleted
        let numToDelete = 0
        for (
          ;
          !isEqual(oldTokens[i + numToDelete], newToken) && numToDelete + i < oldTokens.length;
          numToDelete++
        ) {}
        if (i + numToDelete === oldTokens.length) {
          // Didn't find any match for this token - it must have been an edit. Keep the token and proceed
          newPathInfos.push(this.currentPathInfos[i])
        } else {
          if (i + numToDelete < this.currentPathInfos.length) {
            // It was a delete. Found a match further on in the path. Move the loop pointer
            i += numToDelete
            numDeleted += numToDelete
            newPathInfos.push(this.currentPathInfos[i])
          }
        }
      }
      this.currentPathInfos = newPathInfos
    },

    addSlash() {
      const slashSpan = document.createElement('span')
      slashSpan.appendChild(document.createTextNode('/'))
      slashSpan.className = 'path-slash'
      this.$refs.editor.appendChild(slashSpan)
    },

    describe(token, doNotCapitalizeMatches) {
      try {
        if (!token) return ''
        let description = ''
        let condCap = doNotCapitalizeMatches ? t => t : _.upperFirst

        // description of match
        switch (token.type) {
          case 'empty':
            description += condCap(this.$i18n.t('MATCHES_EMPTY'))
            break
          case 'exact':
            description += condCap(this.$i18n.t('MATCHES_EXACT_MD', { value: token.text }))
            break
          case 'regex':
            description += condCap(this.$i18n.t('MATCHES_REGEX_MD', { value: token.text }))
            break
          case 'wildcard':
            description += condCap(this.$i18n.t('MATCHES_WILDCARD_MD'))
            break
          case 'deepwild':
            return condCap(this.$i18n.t('MATCHES_DEEPWILD_MD'))
          case 'contains':
            description += condCap(this.$i18n.t('MATCHES_CONTAINS_MD', { value: token.text }))
            break
          case 'endswith':
            description += condCap(this.$i18n.t('MATCHES_ENDSWITH_MD', { value: token.text }))
            break
          case 'startswith':
            description += condCap(this.$i18n.t('MATCHES_STARTSWITH_MD', { value: token.text }))
            break
          case 'window':
            description += _.upperFirst(this.$i18n.t('MATCHES_WINDOW_MD', { value: token.title }))
            if (token.global) description += this.$i18n.t('MATCHES_GLOBAL_MD')
            else description += this.$i18n.t('MATCHES_LOCAL_MD')
            return description
          case 'tail':
            if (token.rel) {
              description += this.describe(token.ref)
              switch (token.rel) {
                case 'above':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_ABOVE_MD'))
                  break
                case 'below':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_BELOW_MD'))
                  break
                case 'left-of':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_LEFT_MD'))
                  break
                case 'right-of':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_RIGHT_MD'))
                  break
                case 'sibling-of':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_SIBLING_MD'))
                  break
                case 'nearest':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_NEAREST_MD'))
                  break
                case 'child-of':
                  description += ' ' + _.upperFirst(this.$i18n.t('RELATION_CHILD_MD'))
                  break
              }

              description += ' ' + this.describe(token.target, true)
            } else {
              description += this.describe(token.target)
            }
            if (token.onlyVisible) description += ' ' + _.upperFirst(this.$i18n.t('ONLYVISIBLE_MD'))

            return description
        }

        // hint
        if (token.hint)
          description += ' ' + _.upperFirst(this.$i18n.t('HINT_MD', { value: token.hint }))
        else description += ' ' + _.upperFirst(this.$i18n.t('NO_HINT_MD'))

        // index
        if (token.index >= 1)
          description += ' ' + _.upperFirst(this.$i18n.t('INDEX_MD', { value: token.index }))

        return description
      } catch (err) {
        return err
      }
    },

    insertTokenSpan(token, pathInfo, pathInfoIndex) {
      const span = document.createElement('span')
      span.className = 'token'

      const nodes = createTokenNodes(token, pathInfo ? ' has-path-info' : '')

      _.each(nodes, n => {
        n && span.appendChild(n)
      })

      span.setAttribute('data-index', pathInfoIndex)
      span.setAttribute(
        'title',
        `${this.describe(token)}\n\n${this.$i18n?.t('RIGHT_CLICK_TO_EDIT')}`
      )

      this.$refs.editor.appendChild(span)
    },

    generateAndInsertNewDom(tokens) {
      if (!tokens) tokens = this.currentTokens
      else this.currentTokens = tokens

      tokens.forEach((token, idx) => {
        if (!token) return
        var hasPathInfo = this.currentPathInfos && this.currentPathInfos[idx]
        this.insertTokenSpan(token, hasPathInfo, idx)
        if (token.type !== 'window' && token.type !== 'tail') this.addSlash()
      })
    },

    pathInfoWatchHandler(newVal) {
      this.currentPathInfos = newVal
      this.handleUserChange(this.path, true)
    }
  },

  watch: {
    path(newVal, oldVal) {
      if (this.$refs.editor && newVal != getInnerText(this.$refs.editor)) {
        this.handleUserChange(newVal, true)
        this.closeMenu()
      }
    },

    pathInfos(newVal) {
      this.pathInfoWatchHandler(newVal)
    },
    pathIndexAtCaret(newVal, oldVal) {
      console.debug('new caret at ', newVal)
      this.updateEditor(this.$refs.editor, this.previousPath)
    }
  },

  mounted() {
    if (this.path) {
      // Handle initial prop value if any
      if (this.pathInfos && this.pathInfos.length > 0) this.pathInfoWatchHandler(this.pathInfos)
      else this.handleUserChange(this.path, true)
    }
  }
}

// Recursive visitor/walker
function walk(node, func) {
  var stop = func(node) // Return true to exit out of the recursion
  var children = node.childNodes
  for (
    var i = 0;
    !stop && i < children.length;
    i++ // Children are siblings to each other
  )
    if (walk(children[i], func)) return true
  return stop
}

// It's handy to be able to quickly enable tracing of the cursor tracking as it is a bit brittle
function cDebug(...args) {
  //console.debug(...args)
}

function stripNewlines(s) {
  return s.replace(/\n/g, '')
}

function getInnerText(elm) {
  const inner = stripNewlines(elm.innerText)
  return inner
}

function lastTextChild(elm) {
  if (elm.nodeType === Node.TEXT_NODE) return elm
  for (var i = elm.childNodes.length - 1; i >= 0; i--) {
    let node = elm.childNodes[i]
    let tc = lastTextChild(node)
    if (tc) return tc
  }
  return elm
}

function saveCursorIndex(editor) {
  // Counts the number of characters left of the cursor
  const selection = window.getSelection && window.getSelection()
  if (!selection || selection.rangeCount === 0) return 0
  const r = selection.getRangeAt(0)
  if (r == null || (r.startOffset === 0 && r.startContainer === editor)) return 0
  let elt = r.commonAncestorContainer
  while (elt && elt !== editor && elt !== document.body) elt = elt.parentElement
  if (elt !== editor) return 0
  const textChild = lastTextChild(r.endContainer)
  var count = 0
  cDebug('Walking to find current cursor index', r)
  walk(
    editor,
    elm => {
      if (elm.nodeType === Node.TEXT_NODE) {
        const text = stripNewlines(elm.textContent)
        if (elm !== textChild) {
          cDebug('Visiting non-end text', text, count, text.length)
          count += text.length
        } else {
          cDebug('Visiting end text', text, count, r.endOffset)
          count += r.endOffset
          return true
        }
      }
      return false
    },
    0
  )
  cDebug('Found cursor index to be', count)
  return count
}

function findChildWithCharIndex(charIndex, editor) {
  var child = editor.firstChild,
    charCount = 0,
    offsetInChild = 0
  cDebug('Walking to find element with char index', charIndex)
  walk(editor, elm => {
    if (elm.nodeType === Node.TEXT_NODE) {
      const text = stripNewlines(elm.textContent)
      if (charCount + text.length < charIndex) {
        cDebug('Visiting non-end text', text, charCount, charCount + text.length)
        charCount += text.length
        child = elm
        offsetInChild = text.length
      } else {
        cDebug('Visiting target element', text, charCount)
        child = elm
        offsetInChild = charIndex - charCount
        return true
      }
    }
    return false
  })
  cDebug('offset in child', offsetInChild)
  return { child, offsetInChild }
}

function restoreCursorPosition(savedIndex, editor) {
  if (!savedIndex) return
  editor.focus()
  var s = window.getSelection()
  if (s.rangeCount > 0) s.removeAllRanges()
  var { child, offsetInChild } = findChildWithCharIndex(savedIndex, editor)
  if (child) {
    var r = document.createRange()
    cDebug('Setting offset in element', child.textContent, offsetInChild)
    r.setStart(child, offsetInChild)
    r.setEnd(child, offsetInChild)
    s.addRange(r)
  }
}

const pathRegex = /(\{[^}]*\})?(([^/]*\/?)*)/ // * can't be + because empty tokens are allowed (eg when deleting a middle token)

function getTokensFromPath(path) {
  return path ? parser.parse(path) : []
}

function getPathElementFromToken(t) {
  const hint = t.hint ? `(${t.hint})` : ''
  const index = t.index ? `#${t.index}` : ''

  switch (t.type) {
    case 'empty':
      return ''
    case 'window':
      return `{${t.global ? '!' : ''}${t.title}}`
    case 'endswith':
      return `${hint}*${t.text}${index}`
    case 'startswith':
      return `${hint}${t.text}*${index}`
    case 'contains':
      return `${hint}*${t.text}*${index}`
    case 'regex':
      return `${hint}^${t.text}^${index}`
    case 'exact':
      return `${hint}${t.text}${index}`
    case 'wildcard':
      return `*${index}`
    case 'deepwild':
      return '**'
  }
}

function getPathFromTokens(tokens) {
  let window = ''
  let tail = tokens
  if (tokens?.length > 0 && tokens[0] && tokens[0].type === 'window') {
    window = getPathElementFromToken(tokens[0])
    tail = tokens.slice(1)
  }
  return (
    window +
    _.join(
      _.map(tail, t => {
        switch (t.type) {
          case 'tail':
            if (t.rel && t.ref) {
              return `${getPathElementFromToken(t.ref)}<${t.rel}>${getPathElementFromToken(
                t.target
              )}${t.onlyVisible ? '' : '!'}`
            }
            return getPathElementFromToken(t.target) + (t.onlyVisible ? '' : '!')
          default:
            return getPathElementFromToken(t)
        }
      }),
      '/'
    )
  )
}

function createClassedText(text, className) {
  let e = document.createElement('span')
  e.appendChild(document.createTextNode(text))
  e.className = className
  return e
}

function createHintNode(token, classes) {
  if (token.hint) {
    console.debug('hint classes', token, classes)
    let e = document.createElement('span')
    e.appendChild(createClassedText('(', 'marker'))
    e.appendChild(createClassedText(token.hint, 'text'))
    e.appendChild(createClassedText(')', 'marker'))
    e.className = 'hint' + (classes ?? '')
    return e
  }

  return null
}

function hasIndex(token) {
  return token.index != null && token.index !== 0
}

function createIndexNode(token, classes) {
  if (hasIndex(token)) {
    let e = document.createElement('span')
    e.appendChild(createClassedText('#', 'marker'))
    if (token.index > 0) e.appendChild(createClassedText(token.index, 'text'))
    e.className = 'index' + (classes ?? '')
    return e
  }
  return null
}

function createMatch(type, hasHint, hasIndex, ...nodes) {
  let e = document.createElement('span')
  e.className = 'match ' + (hasHint ? 'has-hint ' : '') + (hasIndex ? 'has-index ' : '') + type
  _.each(nodes, n => n && e.appendChild(n))
  return e
}

function createRelationNode(rel) {
  let e = document.createElement('span')
  e.appendChild(createClassedText('<', 'marker'))
  e.appendChild(createClassedText(rel, 'text'))
  e.appendChild(createClassedText('>', 'marker'))
  e.className = 'relation'
  return e
}

function createOnlyVisibleNode(token, classes) {
  if (token.onlyVisible) return null

  let e = document.createElement('span')
  e.appendChild(createClassedText('!', 'marker'))
  e.className = 'include-invisible' + (classes ?? '')
  return e
}

function createTokenNodes(token, classes) {
  if (!token) return []

  switch (token.type) {
    case 'window':
      return [
        createMatch(
          'window' + (classes ?? ''),
          false,
          false,
          createClassedText('{', 'marker'),
          token.global ? createClassedText('!', 'marker') : null,
          document.createTextNode(token.title),
          createClassedText('}', 'marker')
        )
      ]
    case 'empty':
      return []
    case 'exact':
      return [
        createHintNode(token, classes),
        createMatch(
          'exact' + (classes ?? ''),
          token.hint,
          hasIndex(token),
          document.createTextNode(token.text)
        ),
        createIndexNode(token, classes)
      ]
    case 'deepwild':
      return [
        createMatch('deepwild' + (classes ?? ''), false, false, createClassedText('**', 'wildcard'))
      ]
    case 'wildcard':
      return [
        createMatch(
          'wildcard' + (classes ?? ''),
          false,
          hasIndex(token),
          createClassedText('*', 'wildcard')
        ),
        createIndexNode(token, classes)
      ]
    case 'contains':
      console.debug('contains', token, classes)
      return [
        createHintNode(token, classes),
        createMatch(
          'contains' + (classes ?? ''),
          token.hint,
          hasIndex(token),
          createClassedText('*', 'marker'),
          document.createTextNode(token.text),
          createClassedText('*', 'marker')
        ),
        createIndexNode(token, classes)
      ]

    case 'endswith':
      return [
        createHintNode(token, classes),
        createMatch(
          'endswith' + (classes ?? ''),
          token.hint,
          hasIndex(token),
          createClassedText('*', 'marker'),
          document.createTextNode(token.text)
        ),
        createIndexNode(token, classes)
      ]

    case 'startswith':
      return [
        createHintNode(token, classes),
        createMatch(
          'startswith' + (classes ?? ''),
          token.hint,
          hasIndex(token),
          document.createTextNode(token.text),
          createClassedText('*', 'marker')
        ),
        createIndexNode(token, classes)
      ]

    case 'regex':
      return [
        createHintNode(token, classes),
        createMatch(
          'regex' + (classes ?? ''),
          token.hint,
          hasIndex(token),
          createClassedText('^', 'marker'),
          document.createTextNode(token.text),
          createClassedText('^', 'marker')
        ),
        createIndexNode(token, classes)
      ]
    case 'tail':
      if (token.rel) {
        return [
          ...createTokenNodes(token.ref, classes),
          createRelationNode(token.rel),
          ...createTokenNodes(
            token.target,
            (classes ?? '') + (token.onlyVisible ? '' : ' includes-invisible')
          ),
          createOnlyVisibleNode(token, classes)
        ]
      } else {
        return [
          ...createTokenNodes(
            token.target,
            (classes ?? '') + (token.onlyVisible ? '' : ' includes-invisible')
          ),
          createOnlyVisibleNode(token, classes)
        ]
      }

    default:
      return [createMatch('unknown', false, false, document.createTextNode(token.text))]
  }
}
</script>
